<template>
  <div class="file-list-container">
    <div class="toolbar">
      <div class="toolbar-left">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item
            v-for="(item, index) in pathList"
            :key="index"
            :to="{ path: item.path }"
          >
            {{ item.name }}
          </el-breadcrumb-item>
        </el-breadcrumb>
      </div>

      <div class="toolbar-right">
        <el-input
          v-model="inputData"
          placeholder="搜索我的文件"
          prefix-icon="el-icon-search"
          size="medium"
          class="search-input"
          clearable
          @keyup.enter.native="onSearchFile"
        />
        <el-button type="primary" icon="el-icon-upload" class="action-btn" @click="onClickUpload">上传</el-button>
        <el-button icon="el-icon-folder-add" class="action-btn" @click="onCreateFolder">新建</el-button>
      </div>
    </div>

    <transition name="el-zoom-in-bottom">
      <div v-if="selectedTable.length > 0" class="selection-bar">
        <span class="selection-count">已选择 {{ selectedTable.length }} 项</span>
        <el-divider direction="vertical" />
        <el-button type="text" icon="el-icon-collection" @click="addToAlbum">保存到合集</el-button>
        <el-button type="text" icon="el-icon-rank" @click="moveToFolder">移动</el-button>
        <el-button type="text" icon="el-icon-delete" class="delete-text" @click="onDeleteFile">删除</el-button>
        <el-button type="text" @click="toggleSelection()">取消</el-button>
      </div>
    </transition>

    <div
      v-infinite-scroll="loadMore"
      class="table-wrapper"
      infinite-scroll-disabled="infiniteDisabled"
      infinite-scroll-distance="20"
    >
      <el-table
        ref="multipleTable"
        :data="dataList"
        class="pikpak-table"
        @selection-change="handleTableSectionChange"
      >
        <el-table-column type="selection" width="55" />

        <el-table-column label="文件名" min-width="400">
          <template slot-scope="scope">
            <div class="file-name-cell" @click="onClickFilename(scope.row)">
              <i :class="getFileIcon(scope.row.fileType)" :style="getIconStyle(scope.row.fileType)" />
              <span class="file-text">{{ scope.row.filename }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="size" label="大小" width="120" />
        <el-table-column prop="updateTime" label="修改时间" width="180" align="right" />
      </el-table>

      <div class="load-status">
        <p v-if="loading"><i class="el-icon-loading" /> 加载中...</p>
        <p v-if="noMore && dataList.length > 0">没有更多文件了</p>
        <el-empty v-if="dataList.length === 0 && !loading" description="暂无文件" />
      </div>
    </div>

    <el-dialog
      :visible.sync="showPreviewDialog"
      :before-close="handlePreviewClose"
      width="70%"
      top="5vh"
      custom-class="pikpak-preview-dialog"
      append-to-body
    >
      <div slot="title" class="preview-header">
        <i :class="getFileIcon(fileType)" :style="getIconStyle(fileType)" />
        <span class="preview-filename">{{ fileDetail ? fileDetail.filename : '预览' }}</span>
      </div>

      <div v-if="fileDetail" class="preview-body">
        <template v-if="fileType === 1001">
          <el-image
            :src="fileDetail.url"
            fit="contain"
            class="preview-img"
            :preview-src-list="[fileDetail.url]"
          />
        </template>

        <template v-else-if="fileType === 1002">
          <video
            ref="videoPlayer"
            :src="fileDetail.url"
            class="preview-video"
            controls
            autoplay
          />
        </template>

        <template v-else-if="fileType === 1003">
          <div class="audio-container">
            <i class="el-icon-headset audio-icon" />
            <audio :src="fileDetail.url" controls autoplay class="preview-audio" />
          </div>
        </template>

        <template v-else-if="fileType === 1007">
          <iframe :src="getPdfUrl(fileDetail.url)" width="100%" height="600px" frameborder="0" />
        </template>

        <template v-else>
          <el-empty description="该文件格式暂不支持直接预览">
            <el-button type="primary" plain @click="handleDownload(fileDetail)">下载到本地查看</el-button>
          </el-empty>
        </template>
      </div>

      <div slot="footer" class="preview-footer">
        <div class="file-info">
          <span>大小: {{ fileDetail ? fileDetail.size : '-' }}</span>
          <el-divider direction="vertical" />
          <span>修改时间: {{ fileDetail ? fileDetail.updateTime : '-' }}</span>
        </div>
        <el-button type="primary" round icon="el-icon-download" @click="handleDownload(fileDetail)">下载文件</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="上传文件"
      :visible.sync="showUploadDialog"
      width="560px"
      :before-close="handleUploadClose"
      append-to-body
      custom-class="pikpak-upload-dialog"
    >
      <uploader
        v-if="options"
        :options="options"
        :auto-start="true"
        class="uploader-app"
        @file-added="onFileAdded"
        @file-success="onFileSuccess"
        @file-error="onFileError"
        @complete="onUploadComplete"
      >
        <uploader-unsupport />

        <uploader-drop class="upload-drop-zone">
          <div class="drop-content">
            <i class="el-icon-upload" />
            <p>将文件拖到此处，或 <uploader-btn :attrs="attrs" class="select-btn">点击上传</uploader-btn></p>
            <span class="upload-tip">支持大文件秒传及断点续传</span>
          </div>
        </uploader-drop>

        <uploader-list class="upload-list-view" />
      </uploader>
    </el-dialog>

    <el-dialog
      title="新建文件夹"
      :visible.sync="showCreateFolderDialog"
      width="400px"
      append-to-body
      custom-class="pikpak-dialog"
    >
      <el-form
        ref="folderForm"
        :model="createFolderForm"
        @submit.native.prevent="createFolder"
      >
        <el-form-item
          prop="folderName"
          :rules="[{ required: true, message: '请输入文件夹名称', trigger: 'blur' }]"
        >
          <el-input
            v-model="createFolderForm.folderName"
            placeholder="文件夹名称"
            prefix-icon="el-icon-folder"
            clearable
            autofocus
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button round @click="showCreateFolderDialog = false">取 消</el-button>
        <el-button
          type="primary"
          :loading="loading"
          round
          @click="createFolder"
        >确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  addFile,
  createFolder, deleteFile,
  getAlbumList,
  getDiskChannelInfo,
  getDiskFile,
  getFileDetail,
  getFolderTree, moveFile
} from '@/api/disk'
import { hashFile } from '@/utils/util'

export default {
  name: 'DiskFile',
  data() {
    return {
      // 屏幕宽度, 为了控制分页条的大小
      screenWidth: document.body.clientWidth,
      currentPage: 1,
      pageSize: 10,
      totalSize: 0,
      dataList: [],
      currentPid: '0',
      // 对话框被选中的文件
      selectedTable: [],
      inputData: '',
      showPreviewDialog: false,
      fileDetail: null,
      fileType: 0,
      videoProp: null,
      pathList: [],
      loading: false,
      noMore: false,
      queryForm: {
        pn: 1,
        path: '/',
        fileType: null
      },
      // ****************************************************************************************************************
      showCreateFolderDialog: false,
      createFolderForm: {
        pid: '0',
        folderName: null
      },
      // ****************************************************************************************************************
      showAddToAlbumUploadDialog: false,
      albumList: [],
      // ****************************************************************************************************************
      folderTree: [],
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      showMoveToFolderDialog: false,
      moveFileForm: {
        pid: null,
        fileIds: []
      },
      // ****************************************************************************************************************
      showUploadDialog: false,
      options: null,
      attrs: {
        accept: '*'
      },
      uploadForm: {
        channelCode: null,
        pid: null,
        uploadId: null,
        uploadIds: []
      }
    }
  },
  computed: {
    infiniteDisabled() {
      return this.loading || this.noMore
    }
  },
  created() {
    const pn = this.$route.query.pn
    if (pn !== undefined) {
      this.queryForm.pn = pn
    }

    const path = this.$route.query.path
    if (path !== undefined) {
      this.queryForm.path = path
    }
  },
  methods: {
    loadMore() {
      if (this.infiniteDisabled) return
      this.loading = true

      getDiskFile(this.queryForm).then(resp => {
        if (resp.code === 0) {
          const { list, hasNext } = resp.data.pageList

          // 追加数据而不是覆盖
          this.dataList = [...this.dataList, ...list]

          // 更新路径列表（仅在第一次或路径改变时）
          if (this.queryForm.pn === 1) {
            this.updatePathInfo(resp.data)
          }

          // 判断是否还有更多
          if (!hasNext) {
            this.noMore = true
          } else {
            this.queryForm.pn++
          }
        } else {
          this.noMore = true
          this.$message.error(resp.msg)
        }
      }).finally(() => {
        this.loading = false
      })
    },
    updatePathInfo(respData) {
      const { namePathList } = respData
      this.currentPid = respData.currentPid

      this.pathList = []
      if (!namePathList || namePathList.length === 0) {
        this.pathList.push({ path: '/disk', name: '全部文件' })
      } else {
        // 这里的逻辑要根据你的后端返回格式调整
        namePathList.forEach(item => {
          this.pathList.push({
            path: `/disk?path=${item.path}`,
            name: item.filename
          })
        })
      }
    },
    // 切换目录时重置状态
    changeDirectory(newPath) {
      this.queryForm.path = newPath
      this.queryForm.pn = 1
      this.dataList = []
      this.noMore = false
      this.loadMore()
    },

    // 动态图标映射
    getFileIcon(type) {
      const iconMap = {
        1000: 'el-icon-folder', // Folder
        1001: 'el-icon-picture', // Image
        1002: 'el-icon-video-camera', // Video
        1003: 'el-icon-headset', // Audio
        1004: 'el-icon-document', // Text
        1005: 'el-icon-set-up', // Other (Application / 应用程序/设置)
        1006: 'el-icon-files', // Any (*/* / 所有文件通配)
        1007: 'el-icon-document-checked', // DocPdf (PDF / 带有校验感的特殊文档)
        1008: 'el-icon-document-copy', // DocWord (Word / 复合文档)
        1009: 'el-icon-data-analysis', // DocExcel (Excel / 数据图表)
        1010: 'el-icon-monitor' // DocPpt (PPT / 演示文稿/显示器投影)
      }
      return iconMap[type] || 'el-icon-files'
    },
    getIconStyle(type) {
      const colorMap = {
        1000: '#f59e0b', // Folder (琥珀黄 - 经典的文件夹色)
        1001: '#10b981', // Image (翡翠绿 - 代表多媒体图片)
        1002: '#ef4444', // Video (番茄红 - 视频/播放按钮常用色)
        1003: '#8b5cf6', // Audio (熏衣紫 - 音乐/声波律动色)
        1004: '#6b7280', // Text (中性灰 - 代表纯文本/代码)
        1005: '#3b82f6', // Other/Application (天空蓝 - 操作系统与应用软件常用色)
        1006: '#9ca3af', // Any (浅灰色 - 代表通配或未知类型)
        1007: '#dc2626', // DocPdf (正红色 - 契合 Adobe PDF 的标准标志色)
        1008: '#2563eb', // DocWord (深蓝色 - 契合 Microsoft Word 的官方蓝色)
        1009: '#16a34a', // DocExcel (深绿色 - 契合 Microsoft Excel 的官方绿色)
        1010: '#ea580c' // DocPpt (橘红色 - 契合 Microsoft PPT 的官方橙色)
      }
      return { color: colorMap[type] || '#64748b', fontSize: '24px' }
    },
    // ****************************************************************************************************************
    // Simple Uploader 文件上传
    // ****************************************************************************************************************
    onClickUpload() {
      // 确保上传到当前正在浏览的目录
      this.uploadForm.pid = this.currentPid

      getDiskChannelInfo().then(resp => {
        if (resp.code === 0) {
          const respData = resp.data
          this.uploadForm.channelCode = respData.channelCode
          this.options = {
            target: resp.data.ossUrl,
            chunkSize: 1024 * 1024 * 10, // 手机端改为 5M 切片更稳定
            maxChunkRetries: 2,
            fileParameterName: 'file',
            testChunks: false, // 不发送 GET 请求获取已上传的分片
            // 服务器分片校验函数, 秒传及断点续传基础
            checkChunkUploadedByResponse: (chunk, message) => {
              // 上传文件到 oss 后的响应
              const obj = JSON.parse(message)
              return obj.data.uploaded
              // return (obj.data.uploaded || []).indexOf(chunk.offset + 1) >= 0
            },
            query: () => ({ channelCode: resp.data.channelCode, multiparts: '' }),
            headers: { Authorization: 'Bearer ' + resp.data.token }
          }

          this.showUploadDialog = true
        } else {
          this.$message.error('获取 OSS 服务器地址失败, 暂时无法上传文件')
        }
      }).catch(error => {
        this.$message.error(error.message)
      })
    },
    onFileAdded(file) {
      if (file.file.size > 1024 * 1024 * 1024 * 20) {
        this.$message.warning('上传的文件应小于 20GB')
        return
      }
      file.pause()
      hashFile(file.file).then(res => { file.uniqueIdentifier = res.sha256sum; file.resume() })
    },
    onFileSuccess(rootFile, file, response, chunk) {
      const resp = JSON.parse(response)
      if (resp.code === 0) {
        this.uploadForm.pid = this.currentPid
        this.uploadForm.uploadId = resp.data.uploadId
        addFile(this.uploadForm).then(resp0 => {
          if (resp0.code === 0) {
            this.$message.info('文件 ' + file.file.name + ' 已添加到网盘中')
          } else {
            this.$message.info(resp0.msg)
          }
        }).catch(error => {
          this.$message.error(error.message)
        })
      } else {
        this.$message.error('文件上传失败')
      }
    },
    onFileError(rootFile, file, response, chunk) {
      this.$message.error('文件上传错误')
    },
    // 上传全部完成后，刷新当前目录列表
    onUploadComplete() {
      this.$message.success('所有文件处理完成')
      // 延迟一秒刷新，给后端处理索引留出时间
      setTimeout(() => {
        this.changeDirectory(this.queryForm.path)
      }, 1000)
    },
    // 修改原来的 handleUploadClose
    handleUploadClose() {
      this.showUploadDialog = false
      // 可以在这里提示：关闭弹窗不会停止后台上传（取决于 simple-uploader 配置）
    },
    // ****************************************************************************************************************
    handleDownload(file) {
      if (!file || !file.url) return
      window.open(file.url, '_blank')
    },
    handlePreviewClose() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
      }
      this.showPreviewDialog = false
      this.fileDetail = null
      this.fileType = 0
    },
    onClickFilename(row) {
      this.fileType = row.fileType
      if (this.fileType === 1000) {
        // 文件夹类型
        const newPath = this.queryForm.path === '/'
          ? `/${row.filename}`
          : `${this.queryForm.path}/${row.filename}`

        // 更新路由不刷新页面，而是触发逻辑跳转
        this.$router.push({ query: { path: newPath }})
        this.changeDirectory(this.queryForm.path)
      } else {
        getFileDetail(row.fileId).then(resp => {
          if (resp.code === 0) {
            this.showPreviewDialog = true
            this.fileDetail = resp.data
            if (this.fileType === 1002) {
              this.videoProp = {
                videoUrl: this.fileDetail.url
              }
            }
          } else {
            this.$message.error(resp.msg)
          }
        }).catch(error => {
          this.$message.error(error.message)
        })
      }
    },
    getPdfUrl(url) {
      return '/pdfjs/web/viewer.html?file=' + encodeURIComponent(url)
    },
    // ****************************************************************************************************************
    // 1. 打开对话框
    onCreateFolder() {
      this.createFolderForm.folderName = '' // 清空旧数据
      this.showCreateFolderDialog = true
      // 自动聚焦处理 (可选)
      this.$nextTick(() => {
        this.$refs.folderForm.clearValidate()
      })
    },

    // 2. 执行创建逻辑
    createFolder() {
      this.$refs.folderForm.validate((valid) => {
        if (!valid) return

        this.loading = true
        // 设置父级 ID 为当前目录 ID
        this.createFolderForm.pid = this.currentPid

        createFolder(this.createFolderForm).then(resp => {
          if (resp.code === 0) {
            this.$message.success('创建成功')
            this.showCreateFolderDialog = false
            // 刷新当前列表
            this.changeDirectory(this.queryForm.path)
          } else {
            this.$message.error(resp.msg || '创建失败')
          }
        }).catch(error => {
          this.$message.error(error.message)
        }).finally(() => {
          this.loading = false
        })
      })
    },
    // 处理表格被选中
    handleTableSectionChange(val) {
      this.selectedTable = val
    },
    addToAlbum() {
      getAlbumList(1).then(resp => {
        if (resp.code === 0) {
          this.albumList = resp.data
          this.showAddToAlbumUploadDialog = true
        }
      })
    },
    moveToFolder() {
      for (const item of this.selectedTable) {
        this.moveFileForm.fileIds.push(item.fileId)
      }

      getFolderTree().then(resp => {
        if (resp.code === 0) {
          this.folderTree = resp.data
          this.showMoveToFolderDialog = true
        } else {
          this.$message.error(resp.msg)
        }
      }).catch(error => {
        this.$message.error(error.message)
      })
    },
    handleNodeClick(data) {
      this.moveFileForm.pid = data.fileId
      this.$message.info('移动文件到 ' + data.label + ' 目录')
    },
    onMoveToFolder() {
      moveFile(this.moveFileForm).then(resp => {
        if (resp.code === 0) {
          this.$router.go(0)
        } else {
          this.$message.error(resp.msg)
        }
      }).catch(error => {
        this.$message.info(error.message)
      })
      this.showMoveToFolderDialog = false
      this.toggleSelection()
    },
    onDeleteFile() {
      this.$confirm('确定要删除选中的 ' + this.selectedTable.length + ' 个文件/文件夹?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        var fileIds = []
        for (const item of this.selectedTable) {
          fileIds.push(item.fileId)
        }

        const jsonData = {}
        jsonData.fileIds = fileIds
        deleteFile(jsonData).then(resp => {
          if (resp.code === 0) {
            this.$router.go(0)
          } else {
            this.$message.error(resp.msg)
          }
        }).finally(() => {
          this.toggleSelection()
        })
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消'
        })
      })
    },
    onSearchFile() {
      this.$message.info('search file')
    },
    toggleSelection() {
      this.toggleSelection0(this.selectedTable)
      this.selectedTable = []
    },
    // 取消选中
    toggleSelection0(rows) {
      if (rows) {
        rows.forEach(row => {
          this.$refs.multipleTable.toggleRowSelection(row)
        })
      } else {
        this.$refs.multipleTable.clearSelection()
      }
    },
    handleSelectionChange(val) {
      this.multipleSelection = val
    }
  }
}
</script>

<style scoped>
.file-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* 顶部工具栏 */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-input {
  width: 240px;
  margin-right: 12px;
}
.search-input ::v-deep .el-input__inner {
  border-radius: 20px;
  background-color: #f1f3f4;
  border: none;
}

.action-btn {
  border-radius: 10px;
  font-weight: 500;
}

/* 表格样式重塑 */
.table-wrapper {
  flex-grow: 1;
  overflow-y: auto;
}

.pikpak-table ::v-deep .el-table__row {
  cursor: pointer;
  transition: background 0.2s;
}

.pikpak-table ::v-deep .el-table__row:hover {
  background-color: #f8faff !important;
}

.pikpak-table ::v-deep .el-table__header th {
  background-color: transparent;
  color: #5f6368;
  font-weight: normal;
  border-bottom: 1px solid #f0f0f0;
}

.file-name-cell {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
}

.file-text:hover {
  color: #1a73e8;
}

/* 悬浮操作条 */
.selection-bar {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a1a;
  color: #fff;
  padding: 10px 24px;
  border-radius: 30px;
  z-index: 1000;
  display: flex;
  align-items: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.selection-count {
  font-size: 14px;
  margin-right: 15px;
}

.selection-bar .el-button--text {
  color: #fff;
  margin: 0 10px;
}

.selection-bar .delete-text {
  color: #ff4d4f;
}

.load-status {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 13px;
}

/* Dialog 整体圆角优化 */
::v-deep .pikpak-preview-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

/* 标题栏 */
.preview-header {
  display: flex;
  align-items: center;
  padding-right: 30px;
}
.preview-filename {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-left: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 预览主体内容区 */
.preview-body {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.preview-img {
  max-width: 100%;
  max-height: 70vh;
}

.preview-video {
  width: 100%;
  max-height: 70vh;
  outline: none;
  background: #000;
}

/* 音频样式 */
.audio-container {
  text-align: center;
  padding: 40px;
}
.audio-icon {
  font-size: 60px;
  color: #409EFF;
  margin-bottom: 20px;
  display: block;
}
.preview-audio {
  width: 400px;
}

/* 底部操作区 */
.preview-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}
.file-info {
  font-size: 13px;
  color: #909399;
}

/* 适配移动端 */
@media screen and (max-width: 768px) {
  ::v-deep .pikpak-preview-dialog {
    width: 95% !important;
  }
  .preview-audio {
    width: 100%;
  }
}

/* 上传弹窗样式 */
::v-deep .pikpak-upload-dialog {
  border-radius: 16px;
}

.upload-drop-zone {
  border: 2px dashed #dcdfe6;
  border-radius: 12px;
  background-color: #f8f9fa;
  padding: 40px 0;
  text-align: center;
  transition: border-color 0.3s;
}

.upload-drop-zone:hover {
  border-color: #1a73e8;
}

.drop-content i {
  font-size: 48px;
  color: #1a73e8;
  margin-bottom: 16px;
}

.select-btn {
  background: none;
  border: none;
  color: #1a73e8;
  font-weight: bold;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  display: block;
  margin-top: 10px;
}

/* 上传列表美化 */
.upload-list-view {
  max-height: 300px;
  overflow-y: auto;
  margin-top: 20px;
}

/* 隐藏 simple-uploader 默认的一些难看按钮 */
::v-deep .uploader-file-icon {
  display: none;
}
::v-deep .uploader-file-actions > span {
  margin-right: 6px;
  cursor: pointer;
}

/* 复用之前的 pikpak-dialog 样式，或者单独微调 */
::v-deep .pikpak-dialog {
  border-radius: 16px;
}

::v-deep .pikpak-dialog .el-dialog__body {
  padding: 10px 25px;
}

/* 底部按钮平铺或靠右 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 输入框聚焦时的动画感 */
::v-deep .el-input__inner:focus {
  background-color: #fff;
  border-color: #1a73e8;
}
</style>
